import { Fragment, type ReactNode } from 'react';

/** CSS class applied to highlighted spans — styled by the post card (see PostCard). */
export const SEARCH_HIGHLIGHT_CLASS = 'post-search-hl';

// Only highlight once the term is meaningful — single characters would paint
// nearly everything.
const MIN_HIGHLIGHT_LENGTH = 2;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Wrap case-insensitive matches of `query` in `<mark class="post-search-hl">`,
 * touching only the text between tags so HTML markup (and attribute values)
 * stays intact. `html` must already be sanitized — the inserted `<mark>` is
 * static and the matched text comes from that same sanitized string, so no
 * re-sanitization is needed.
 */
export const highlightHtml = (html: string, query: string | undefined): string => {
  const q = (query ?? '').trim();
  if (q.length < MIN_HIGHLIGHT_LENGTH) {
    return html;
  }
  const matcher = new RegExp(escapeRegExp(q), 'gi');
  // Split into tag chunks (kept as-is) and text chunks (highlighted).
  return html.replace(/(<[^>]+>)|([^<]+)/g, (_full, tag: string | undefined, text: string | undefined) => {
    if (tag) {
      return tag;
    }
    return (text ?? '').replace(matcher, match => `<mark class="${SEARCH_HIGHLIGHT_CLASS}">${match}</mark>`);
  });
};

/**
 * Render `text` with case-insensitive matches of `query` wrapped in `<mark>`.
 * For plain-text fields (e.g. a post title) where we control the nodes directly.
 */
export const HighlightedText = ({ text, query }: { text: string; query: string | undefined }): ReactNode => {
  const q = (query ?? '').trim();
  if (q.length < MIN_HIGHLIGHT_LENGTH) {
    return text;
  }
  // One capture group → matched substrings land at odd indices of the split.
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'gi'));
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      // oxlint-disable-next-line react/no-array-index-key
      <mark key={i} className={SEARCH_HIGHLIGHT_CLASS}>
        {part}
      </mark>
    ) : (
      // oxlint-disable-next-line react/no-array-index-key
      <Fragment key={i}>{part}</Fragment>
    ),
  );
};
